import React, { useState, useEffect, useCallback } from "react";
import { Search, Loader2, AlertCircle, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useAvailableInternalChatUsers } from "@/hooks/ping/useInternalChats";
import { useCreateInternalChat } from "@/hooks/ping/useInternalChatMutations";
import type { InternalChatUser } from "@/types";

// Helper function to get user initials
const getUserInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Helper function to get avatar gradient
const getAvatarGradient = (name: string) => {
  const gradients = [
    "from-violet-500 to-violet-600",
    "from-blue-500 to-blue-600",
    "from-emerald-500 to-emerald-600",
    "from-amber-500 to-amber-600",
    "from-rose-500 to-rose-600",
    "from-cyan-500 to-cyan-600",
    "from-indigo-500 to-indigo-600",
    "from-fuchsia-500 to-fuchsia-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

// Helper function to format role label
const formatRoleLabel = (role: string): string => {
  return role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

interface AddInternalChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onChatSelect: (chatId: string) => void;
}

export const AddInternalChatDialog = React.memo(
  ({ isOpen, onClose, onChatSelect }: AddInternalChatDialogProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedPage, setSelectedPage] = useState(1);

    // Fetch available users based on search query
    const {
      data: users = [],
      isLoading,
      error,
      refetch,
    } = useAvailableInternalChatUsers({
      search: searchQuery || undefined,
      page: selectedPage,
      page_size: 50,
    });

    const createChatMutation = useCreateInternalChat();

    // Reset state when dialog opens
    useEffect(() => {
      if (isOpen) {
        setSearchQuery("");
        setSelectedPage(1);
        refetch();
      }
    }, [isOpen, refetch]);

    // Handle user selection
    const handleUserSelect = useCallback(
      async (user: InternalChatUser) => {
        try {
          const newChat = await createChatMutation.mutateAsync({
            participant_id: user.id,
          });

          // Notify parent component
          onChatSelect(newChat.id);

          // Close dialog
          onClose();
        } catch (err) {
          console.error("Error creating internal chat:", err);
          // Error toast is handled by the mutation
        }
      },
      [createChatMutation, onChatSelect, onClose]
    );

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 pb-3 border-b border-border">
            <div className="flex items-center justify-between">
              <DialogTitle className="z-h2 flex items-center gap-2 text-foreground">
                <MessageSquarePlus className="h-5 w-5 text-primary" />
                New Team Chat
              </DialogTitle>
            </div>
          </DialogHeader>

          {/* Search */}
          <div className="p-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
              <Input
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="z-body h-10 pl-9"
                disabled={isLoading || createChatMutation.isPending}
              />
            </div>
          </div>

          {/* User List */}
          <div className="max-h-[400px] overflow-y-auto px-4 pb-4">
            {error && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <span className="z-body mb-3 text-destructive">
                  Failed to load team members
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="z-button"
                  onClick={() => refetch()}
                >
                  Try Again
                </Button>
              </div>
            )}

            {(isLoading || createChatMutation.isPending) &&
              users.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 space-y-3">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="z-body text-muted-foreground">
                    Loading team members...
                  </span>
                </div>
              )}

            {!isLoading && !error && users.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <Search className="h-6 w-6 text-muted-foreground/50" />
                </div>
                <p className="z-body-md mb-1 text-foreground">
                  {searchQuery
                    ? "No team members found"
                    : "No team members available"}
                </p>
                <p className="z-caption text-muted-foreground">
                  {searchQuery
                    ? "Try a different search term"
                    : "Team member list is empty"}
                </p>
              </div>
            )}

            {users.length > 0 && (
              <div className="space-y-1">
                {users.map((user) => {
                  const userName =
                    user.full_name || `${user.first_name} ${user.last_name}`;
                  const initials = getUserInitials(
                    user.first_name,
                    user.last_name
                  );

                  return (
                    <div
                      key={user.id}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer transition-all duration-150",
                        "border border-transparent hover:border-border hover:bg-muted/50"
                      )}
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "z-button flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-white shadow-sm",
                            getAvatarGradient(userName)
                          )}
                        >
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="z-body-md truncate text-foreground">
                              {userName}
                            </h3>
                            <Badge
                              variant="outline"
                              className="z-caption shrink-0 capitalize"
                            >
                              {formatRoleLabel(user.user_type)}
                            </Badge>
                          </div>
                          <p className="z-secondary mt-0.5 truncate text-muted-foreground">
                            {user.email || "No email provided"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

AddInternalChatDialog.displayName = "AddInternalChatDialog";
